import { useMediaQuery, useTheme } from '@mui/material';
import { Separator } from 'react-resizable-panels';

import { useFeatureFlags } from 'src/context';

export const ResizeHandle = ({
  className,
  restColorClass,
  restColor,
  activeColor,
}: {
  className?: string;
  /**
   * Overrides the resting (non-hover) divider color; hover/drag feedback is
   * left untouched. Defaults to the sidebar/legacy gray.
   */
  restColorClass?: string;
  /**
   * Runtime colors for a handle that sits inside a branded surface (the client
   * sidebar): the resting color and the hover/drag color. Each falls back to
   * the neutral gray.
   */
  restColor?: string;
  activeColor?: string;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { EnableNewSidebar } = useFeatureFlags();

  if (isMobile) {
    return null;
  }

  // Runtime colors go through CSS variables because Tailwind needs the class
  // names to be static; the variant classes below are spelled out per branch
  // for the same reason.
  const restBg = restColor
    ? 'bg-(--resize-handle-rest)'
    : (restColorClass ?? (EnableNewSidebar ? 'bg-gray-100' : 'bg-gray-200'));
  // v4 renamed the state attribute (drag → active) and keeps the handle in a
  // "focus" state after a drag, so focus gets the same feedback color as hover.
  const activeClasses = activeColor
    ? 'data-[separator=hover]:bg-(--resize-handle-active) data-[separator=active]:bg-(--resize-handle-active) data-[separator=focus]:bg-(--resize-handle-active)'
    : EnableNewSidebar
      ? 'data-[separator=hover]:bg-gray-400 data-[separator=active]:bg-gray-400 data-[separator=focus]:bg-gray-400'
      : 'data-[separator=hover]:bg-gray-500 data-[separator=active]:bg-gray-500 data-[separator=focus]:bg-gray-500';

  // disableDoubleClick keeps v2 behavior: v4 otherwise resets the layout to
  // its default size on double-click.
  return (
    <Separator
      disableDoubleClick
      className={`w-px h-full ${restBg} ${activeClasses} cursor-col-resize ${className ?? ''}`}
      style={
        {
          '--resize-handle-rest': restColor,
          '--resize-handle-active': activeColor,
        } as React.CSSProperties
      }
    />
  );
};
